/**
 * localizedToast — the ONE sanctioned `sonner` entry point (ADR 0143, T3).
 *
 * Direct `import { toast } from 'sonner'` is banned repo-wide
 * (`no-restricted-imports`, see eslint.config.mjs) so a toast message can never
 * be a raw, untranslated string. Every method here takes [`LocalizedText`], so
 * the message MUST be minted via `t('key')` (translated UI copy) or
 * `plainText(data)` (verbatim data) — a hardcoded English literal or a raw
 * `err.message` passed to a toast is a compile error. This is the toast
 * counterpart of the branded `SeoMeta` / a11y / prop layers.
 */
import type { LocalizedText } from '@letsgethome/types/branded';
// eslint-disable-next-line no-restricted-imports -- this wrapper IS the one sanctioned sonner entry point
import { toast } from 'sonner';

/** sonner's per-call options (duration, action, …), passed through verbatim. */
type ToastOptions = Parameters<typeof toast.success>[1];

export const localizedToast = {
  success: (message: LocalizedText, opts?: ToastOptions) => toast.success(message, opts),
  error: (message: LocalizedText, opts?: ToastOptions) => toast.error(message, opts),
  info: (message: LocalizedText, opts?: ToastOptions) => toast.info(message, opts),
  warning: (message: LocalizedText, opts?: ToastOptions) => toast.warning(message, opts),
  message: (message: LocalizedText, opts?: ToastOptions) => toast.message(message, opts),
  /** Dismiss a toast (or all). No message, so no localization concern. */
  dismiss: (id?: string | number) => toast.dismiss(id),
} as const;
